// @flow
import type { Pet } from '@rover/react-lib/types';
import type {
  PetDetail,
  PetDetailForDisplay,
  PetDetailValueOption,
  PetDetailOption,
} from '@rover/react-lib/types/Pet';
import { kebabifyString } from '@rover/utilities/object-utilities';

// Returns the pet names in a comma separated list
export const petNameListify = (petNames: Array<string>): string =>
  petNames.length > 0 ? petNames.join(', ') : '';

const getSpayedOrNeutered = (pet: Pet): string => (pet.gender === 'm' ? 'Neutered' : 'Spayed');

// Returns keys and display text for the pet details that are stored on the Pet object
export const getPetInformationKeys = (pet: Pet) => [
  { key: 'houseTrained', display: 'House Trained' },
  { key: 'spayedNeutered', display: getSpayedOrNeutered(pet) },
  { key: 'playsChildren', display: 'Gets along with children' },
  { key: 'playsDogs', display: 'Gets along with dogs' },
  { key: 'playsCats', display: 'Gets along with cats' },
  { key: 'implant', display: 'Microchipped' },
];

// Returns a list of PetDetail objects for each property currently stored on the Pet object
// but not yet stored as a PetDetail
export const getPetDetailsFromPetInformation = (pet: Pet): Array<PetDetailForDisplay> => {
  const { petDetails } = pet;
  if (!petDetails) {
    return [];
  }
  const newKeys = getPetInformationKeys(pet)
    // Only return properties where a corresponding PetDetail doesn't exist
    .filter(detail => !petDetails.some(pd => pd.slug === kebabifyString(detail.key)))
    // Only return properties where the value is not null
    .filter(
      detail => Object.prototype.hasOwnProperty.call(pet, detail.key) && pet[detail.key] !== null
    );

  return newKeys.map(detail => {
    const value = pet[detail.key] ? 'yes' : 'no';

    return {
      slug: kebabifyString(detail.key),
      value,
      displayName: detail.display,
      valueDisplayName: `${value[0].toUpperCase()}${value.slice(1).toLowerCase()}`,
      additionalInformation: pet[`${detail.key}Desc`] || '',
    };
  });
};

export const createPetDetailFromOption = (
  value: string,
  additionalInformation: string = '',
  petDetailOption: PetDetailOption
): PetDetail => {
  const { slug, displayName, serviceTypes, multiselect } = petDetailOption;
  const { displayName: valueDisplayName } =
    petDetailOption.options.find((o: PetDetailValueOption) => o.value === value) || {};

  return {
    slug,
    value,
    additionalInformation,
    displayName,
    serviceTypes,
    multiselect,
    valueDisplayName: valueDisplayName || '',
  };
};

export default {
  petNameListify,
  getPetInformationKeys,
  getPetDetailsFromPetInformation,
  createPetDetailFromOption,
};



// WEBPACK FOOTER //
// ../react-lib/src/utils/pets.js